'use client';

import { Button } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import { useToast } from '@common/hooks';
import SvgCheck from '@common/icon/SvgCheck';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { TERM_CATEGORY } from '@/api';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useGlobalStore } from '@/stores/GlobalStore';
import {
  closeResponsiveDialog,
  type ResponsiveDialogConfig,
} from '@/stores/ResponsiveDialogStore';

const MARKETING_CHANNELS = ['앱푸시', '이메일', 'SMS,알림톡'];

export const BENEFIT_CONSENT_TITLE =
  '웰포인트와 행운권 지급을 위해\n동의가 필요해요.';

interface MarketingConsentSheetProps {
  agreeLabel: string;
  onAgree: () => Promise<unknown>;
  successMessage?: string;
  isCloseButtonHidden?: boolean;
}

export const MarketingConsentSheet = ({
  agreeLabel,
  onAgree,
  successMessage = `마케팅 수신 동의 ${dayjs().format('YYYY.MM.DD')}`,
  isCloseButtonHidden = false,
}: MarketingConsentSheetProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [setTermsDrawerType, setTermsDrawerMode] = useGlobalStore(
    useShallow(
      (state) => [state.setTermsDrawerType, state.setTermsDrawerMode] as const,
    ),
  );

  const checkClassName = isChecked ? 'text-Gray-800' : 'text-Gray-300';

  const handleAgree = () => {
    setIsChecked(true);
    setIsSubmitting(true);
    onAgree()
      .then(() =>
        toast({
          message: successMessage,
          type: 'success',
        }),
      )
      .catch(() => setIsSubmitting(false));
  };

  //* bottom 모드여야 약관 드로워가 이 시트 위(z-70)에 뜬다
  const openMarketingTerms = () => {
    setTermsDrawerMode('bottom');
    setTermsDrawerType(TERM_CATEGORY.MARKETING_TERMS);
  };

  return (
    <div
      className={cn('flex flex-col', isDesktopViewport ? 'gap-32' : 'gap-24')}
    >
      <div className="flex flex-col gap-12">
        <div className="relative">
          <button
            role="checkbox"
            aria-checked={isChecked}
            disabled={isSubmitting}
            onClick={() => setIsChecked(!isChecked)}
            className={cn(
              'flex w-full items-center gap-4 rounded-small bg-Gray-25 p-12 pr-48',
              'transition-colors duration-150 motion-reduce:transition-none',
              'enabled:hover:bg-Gray-50 enabled:active:bg-Gray-100',
              FOCUS_RING,
            )}
          >
            <SvgCheck className={cn('p-2', checkClassName)} />
            <span className="font-700 text-Gray-800 typo-body3">
              [선택] 마케팅 정보 수신 동의
            </span>
          </button>
          <button
            aria-label="마케팅 정보 수신 동의 약관 보기"
            onClick={openMarketingTerms}
            className={cn(
              'absolute right-4 top-1/2 -translate-y-1/2 rounded-xsmall p-8 text-Gray-550 transition-colors',
              'hover:text-Gray-700 active:text-Gray-800',
              'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-B-500',
            )}
          >
            <SvgChevronright className="p-5" />
          </button>
        </div>
        <ul className="flex flex-col gap-4 pl-16">
          {MARKETING_CHANNELS.map((channel) => (
            <li
              key={channel}
              className="flex items-center gap-8 p-4 text-Gray-800 typo-caption1"
            >
              <SvgCheck className={cn('size-20 p-2', checkClassName)} />
              {channel}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex gap-8">
        {isCloseButtonHidden ? null : (
          <Button
            theme={isDesktopViewport ? 'sub-fill' : 'sub-line'}
            className={isDesktopViewport ? 'flex-1' : 'w-108'}
            onClick={closeResponsiveDialog}
          >
            닫기
          </Button>
        )}
        <Button
          className="flex-1"
          disabled={isSubmitting}
          onClick={handleAgree}
        >
          {agreeLabel}
        </Button>
      </div>
    </div>
  );
};

export const createMarketingConsentDialog = ({
  title,
  ...props
}: MarketingConsentSheetProps & { title: string }): ResponsiveDialogConfig => ({
  component: MarketingConsentSheet,
  title,
  modalHeader: title,
  drawerType: 'bottom',
  props,
});
